import { useState, useEffect, useRef, useMemo } from 'react';
import { type GeolocationPosition, type TenantApp } from '@inloco/shared';
import { useNotifications } from '../context';
import { useTranslation } from "react-i18next";
import KalmanFilter from 'kalmanjs';

export interface UseGeolocationReturn {
    position: GeolocationPosition | null;
    geolocationStatus: boolean;
    loading: boolean;
}

interface UseGeolocationProps {
    app: TenantApp | null;
    options?: PositionOptions;
}

const getErrorMessage = (error: GeolocationPositionError): string => {
    switch (error.code) {
        case error.PERMISSION_DENIED: return 'geolocation.permissionDenied';
        case error.POSITION_UNAVAILABLE: return 'geolocation.positionUnavailable';
        case error.TIMEOUT: return 'geolocation.timeout';
        default: return 'geolocation.error';
    }
};

const roundCoordinate = (coord: number): number => Math.round(coord * 1e6) / 1e6;
const roundAccuracy = (accuracy: number): number => Math.round(accuracy * 10) / 10;

export const useGeolocation = ({ app, options }: UseGeolocationProps): UseGeolocationReturn => {
    const [position, setPosition] = useState<GeolocationPosition | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [geolocationStatus, setGeolocationStatus] = useState<boolean>(false);
    const { t } = useTranslation();

    const { addNotification } = useNotifications();
    const hasNotifiedError = useRef<boolean>(false);

    const isMobile = useMemo(() =>
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
        , []);

    const internalOptions = useMemo(() => ({
        // su mobile è particolarmente importante cercare di ottenere la posizione più accurata possibile, anche a costo di consumare più batteria
        enableHighAccuracy: options?.enableHighAccuracy ?? true,
        // su mobile accettiamo timeout più lunghi perché il GPS è meno preciso e potrebbe impiegare più tempo a fornire una posizione accurata
        timeout: options?.timeout ?? 10000,
        // i dispositivi mobili tendono ad avere GPS meno precisi, quindi accettiamo posizioni più vecchie
        maximumAge: isMobile ? 60000 : 5000,
    }), [options?.enableHighAccuracy, options?.timeout, isMobile]);

    // Inizializziamo i filtri. 
    // R sarà sovrascritto, Q (process noise) determina la reattività.
    const kalmanLat = useRef(new KalmanFilter({ Q: 0.001 }));
    const kalmanLng = useRef(new KalmanFilter({ Q: 0.001 }));

    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.geolocation || !app) return;

        const watchId = navigator.geolocation.watchPosition(
            (pos) => {
                setLoading(false);
                const { latitude, longitude, accuracy } = pos.coords;

                // Ignora posizioni con accuracy maggiore di 25 metri
                /* if (accuracy > 25) {
                    return;
                } */

                // calcolo del fattore R basato sull'accuratezza.
                // Più accuracy è alta (metri), più R è alto, più il filtro "smussa".
                // Dividiamo per un fattore (es. 100000) per portarlo in scala con i gradi decimali.
                const dynamicR = Math.pow(accuracy, 2) / 100000;

                // passo dynamicR come secondo argomento al metodo filter()
                const filteredLat = kalmanLat.current.filter(latitude, dynamicR);
                const filteredLng = kalmanLng.current.filter(longitude, dynamicR);

                const newPos = {
                    lt: roundCoordinate(filteredLat),
                    lg: roundCoordinate(filteredLng),
                    ac: roundAccuracy(accuracy)
                };

                // Con il filtro Kalman meglio non usare filtri di MIN_DISTANCE o MIN_TIME 
                // perché il filtro stesso smussa le variazioni e fornisce una posizione più stabile.
                // Aggiorniamo sempre lo stato in modo che Kalman e l'animazione
                // possano lavorare su ogni singolo micro-aggiornamento continuo.
                setPosition(newPos);
                setGeolocationStatus(true);
                hasNotifiedError.current = false;
            },
            (err) => {
                setPosition(null);
                setGeolocationStatus(false);

                if (!hasNotifiedError.current && err.code !== err.TIMEOUT) {
                    const msg = t(getErrorMessage(err));
                    addNotification({ message: msg, type: 'error', duration: 0 });
                    hasNotifiedError.current = true;
                }
            },
            internalOptions
        );

        return () => navigator.geolocation.clearWatch(watchId);
    }, [internalOptions, app, addNotification, t]);

    return { position, geolocationStatus, loading };
};